/**
 * One send in the Dispatch log (plan 2026-09-27-003, U6; the approved
 * prototype `plans/2026-09-27-002-dispatch-log-redesign-prototype.html`).
 *
 * Plan 2026-10-14-002 U0a: the card is driven by the server's SUMMARY, not by
 * every row. The rows of an open card load a page at a time
 * (`useSendLogRows`), filtered on the server, with "Load more".
 *
 *   header  "Sent Sep 26, 6:41 PM · 12 people", the running "Built · with Clay
 *           · confirmed of N" line while the send is going, one badge per
 *           outcome bucket (`summary.outcomes`), and "Retry all stopped or
 *           failed (n)";
 *   filters one Glaze `Chip` per state present (plus All), counted from
 *           `summary.byStatus` — a filter whose state has emptied falls back to
 *           All (derived, not an effect);
 *   table   Glaze `Table dense`: chevron / Person / Channel / Status.
 *
 * Every rep-facing word comes from `dispatch-rep-status.ts`, the ONE
 * classifier — this file only lays it out. Used for real sends and test
 * sends alike (`isTest`).
 *
 * The card also owns the send's automatic Clay re-check (`useSendRecheck`):
 * it fires when the card MOUNTS, open or not, so a rep who opens the play sees
 * stuck rows resolve without expanding anything.
 */
import { useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { Chip } from '@clay-run/glaze/chip';
import {
  CollapsibleCard,
  CollapsibleCardBody,
  CollapsibleCardHeader,
} from '@clay-run/glaze/collapsible-card';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';

import {
  REP_STATUS_CHIP_LABEL,
  REP_STATUS_ORDER,
  CLAY_FAILURE_RETRY_RISK,
  classifyRepStatus,
  summaryLine,
  type RepDispatchStatus,
} from '../../../dispatch-rep-status';
import { sendOutcomeBadges, sendPeopleLabel, sendProgressLine } from '../../../send-summary';
import type { PlaybooksSendSummary } from '../../../schemas';
import { ConfirmActionDialog } from '../../ConfirmActionDialog';

import { SendLogRow } from './SendLogRow';
import { useSendLogRows } from './useSendLogRows';
import { useSendRecheck } from './useSendRecheck';
import { useSendRetry, type SendRetry } from './useSendRetry';

export interface SendLogCardProps {
  playId: string;
  /** `null` for a send that predates the dispatch log — nothing per person to show. */
  dispatchId: string | null;
  sentAt: string;
  /** The server's summary of this send — what the header, chips and buttons read. */
  summary: PlaybooksSendSummary;
  isTest: boolean;
  /** How many people the play routed when this test picked a sample of them; `null` = everyone. */
  sampledFrom?: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** `Sep 26, 6:41 PM` — the prototype's format. */
export function formatSentAt(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

type Filter = RepDispatchStatus | 'all';

export function SendLogCard({ playId, dispatchId, sentAt, summary, isTest, sampledFrom = null, open, onOpenChange }: SendLogCardProps) {
  const [filter, setFilter] = useState<Filter>('all');

  const counts = summary.byStatus;
  const present = REP_STATUS_ORDER.filter((status) => (counts[status] ?? 0) > 0);
  // A filter whose state has emptied (a retry landed, a re-check resolved)
  // falls back to All — derived here, never synced by an effect.
  const activeFilter: Filter = filter !== 'all' && (counts[filter] ?? 0) === 0 ? 'all' : filter;
  const activeTotal = activeFilter === 'all' ? summary.total : (counts[activeFilter] ?? 0);

  const log = useSendLogRows({ playId, dispatchId, status: activeFilter, enabled: open, live: summary.live });
  const retry = useSendRetry(playId, dispatchId, log.rows);
  const recheck = useSendRecheck(playId, dispatchId, summary.needsRecheck);

  // `Date.now()` at render: the sends poll re-renders this while anything is
  // live; once settled the age stops mattering.
  const nowMs = Date.now();
  const entries = log.rows.map((row) => ({ row, view: classifyRepStatus(row, nowMs) }));
  const people = sendPeopleLabel(summary.total, sampledFrom);
  const progress = sendProgressLine(summary);
  const badges = sendOutcomeBadges(summary.outcomes);

  return (
    <div data-send-card="true">
      <CollapsibleCard open={open} onOpenChange={onOpenChange} className="border-border-secondary">
        <CollapsibleCardHeader>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Text size="sm" weight="medium">
              {`${isTest ? 'Test · ' : ''}Sent ${formatSentAt(sentAt)} · ${people}`}
            </Text>
            {progress !== null && (
              <Text size="xs" variant="tertiary">
                {progress}
              </Text>
            )}
            {badges.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {/* The whole summary as one sentence for assistive tech (and tests). */}
                <span className="sr-only">{summaryLine(counts)}</span>
                {badges.map((badge) => (
                  <StatusBadge key={badge.key} status={badge.status} size="sm">
                    {badge.text}
                  </StatusBadge>
                ))}
              </div>
            )}
          </div>
          {dispatchId !== null && (
            <div className="flex shrink-0 items-center gap-1">
              <RetryAllStoppedOrFailed retry={retry} summary={summary} />
            </div>
          )}
        </CollapsibleCardHeader>
        <CollapsibleCardBody className="flex flex-col gap-0 border-border-secondary p-0">
          {dispatchId === null && (
            <Text size="xs" variant="tertiary" className="p-3">
              This send predates the dispatch log — nothing to show per person.
            </Text>
          )}
          {dispatchId !== null && summary.total === 0 && (
            <Text size="xs" variant="tertiary" className="p-3">
              No one in this send yet.
            </Text>
          )}
          {summary.total > 0 && (
            <>
              <div className="flex flex-wrap gap-1.5 border-b border-border-secondary px-3 py-2">
                <FilterChip label="All" count={summary.total} active={activeFilter === 'all'} onClick={() => setFilter('all')} />
                {present.map((status) => (
                  <FilterChip
                    key={status}
                    label={REP_STATUS_CHIP_LABEL[status]}
                    count={counts[status] ?? 0}
                    active={activeFilter === status}
                    onClick={() => setFilter(status)}
                  />
                ))}
              </div>
              <Table dense bleed>
                <TableHead>
                  <TableRow>
                    <TableHeader width={28}>
                      <span className="sr-only">Expand</span>
                    </TableHeader>
                    <TableHeader>Person</TableHeader>
                    <TableHeader>Channel</TableHeader>
                    <TableHeader>Status</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {log.isLoading && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <div className="flex justify-center py-4">
                          <LoadingSpinner size="sm" />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!log.isLoading && !log.isError && entries.length === 0 && !log.hasNextPage && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Text size="xs" variant="tertiary" className="py-4 text-center">
                          No one in this state.
                        </Text>
                      </TableCell>
                    </TableRow>
                  )}
                  {entries.map(({ row, view }) => (
                    <SendLogRow key={row.id} playId={playId} row={row} view={view} retry={retry} recheck={recheck} />
                  ))}
                </TableBody>
              </Table>
              {log.isError && (
                <div className="flex items-center justify-between gap-2 border-t border-border-secondary px-3 py-2">
                  <Text size="xs" variant="critical">
                    Couldn&apos;t load the people in this send.
                  </Text>
                  <Button variant="secondary" size="xs" onClick={log.retryLoad}>
                    Try again
                  </Button>
                </div>
              )}
              {entries.length > 0 && (
                <div className="flex items-center justify-between gap-2 border-t border-border-secondary px-3 py-2">
                  <Text size="xs" variant="tertiary">
                    {`Showing ${entries.length.toLocaleString('en-US')} of ${activeTotal.toLocaleString('en-US')}`}
                  </Text>
                  {log.hasNextPage && (
                    <Button variant="secondary" size="xs" disabled={log.isFetchingNextPage} onClick={log.loadMore}>
                      {log.isFetchingNextPage && <LoadingSpinner size="sm" />}
                      Load more
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </CollapsibleCardBody>
      </CollapsibleCard>
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  // Glaze `Chip` has no selected state; the active filter is the blue chip.
  // (A Terra override of Chip's color, flagged in the plan's Glaze list.)
  return (
    <Chip size="sm" color={active ? 'blue' : 'default'} onClick={onClick}>
      {label}
      <span className="ml-1 text-content-tertiary">{String(count)}</span>
    </Chip>
  );
}

/**
 * "Retry all stopped or failed (n)" + its confirmation — shown in a send card's
 * header.
 *
 * The label counts BOTH retryable arms (a send Clay stopped before delivery and
 * a send that never started), which is why it no longer says "failed" on its
 * own: on a card whose only retryable rows are stops, "failed" would be a word
 * for something that did not happen. Both the label and the confirm body are
 * authored in `docs/playbooks-sharing-rules.md` edge case (m). [R20]
 */
function RetryAllStoppedOrFailed({
  retry,
  summary,
}: {
  retry: SendRetry;
  summary: PlaybooksSendSummary;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  // From the server's summary (U0a): the rows are paged, so the client cannot
  // count them. A retry's own refresh of the summary updates this.
  const count = summary.retryable;
  if (count === 0 && !confirmOpen) return null;
  const members = `${count} member${count === 1 ? '' : 's'}`;
  // Sharing rules (o) and (u): rows that errored inside Clay, on a REAL send —
  // retrying those is the one thing here that could send twice, so the confirm
  // says so, with how many. (A test send never delivers anything.) The wire
  // field keeps its old name, `retryableCrashes`; it counts every such row.
  const risky = summary.retryableCrashes;
  const riskSentence = risky === 0 ? '' : ` ${String(risky)} of them errored inside Clay. ${CLAY_FAILURE_RETRY_RISK}`;

  return (
    <>
      <Button variant="danger-secondary" size="sm" disabled={retry.bulkPending} onClick={() => setConfirmOpen(true)}>
        {`Retry all stopped or failed (${count})`}
      </Button>
      <ConfirmActionDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Retry these sends?"
        description={`For a send Clay stopped, Terra rebuilds the message from the play as it is now and sends that. For a send that never started, Terra checks with Clay first and resends only to people Clay has no run for — anyone Clay already has is marked as sent instead. ${members} in this send can be retried.${riskSentence}`}
        confirmLabel={`Retry ${members}`}
        pending={retry.bulkPending}
        onConfirm={() => {
          void retry.retryAll().then(() => setConfirmOpen(false));
        }}
      />
    </>
  );
}
